import { ref, watch } from 'vue'
import { usePedidoStore } from '@/stores/pedido'
import { useRevendedorStore } from '@/stores/revendedor' // Add this import
import useConfirmation from '@/composables/useConfirmation'

// Criar estado compartilhado fora da função para garantir que todas as instâncias compartilhem os mesmos refs
const showCopyNotification = ref(false)
const copyMessage = ref('')

// Estados de carregamento também compartilhados entre instâncias
const loadNotificarPedido = ref(false)
const loadNotificarEstouNaPorta = ref(false)
const loadingClienteBlock = ref(false)
const loadingRevendedorAction = ref(false)
const pendingLoadingStates = ref(new Map())

// Adicionar refs adicionais para o modal de rejeição
const rejectionModalOpen = ref(false)
const pedidoToReject = ref(null)
const rejectionLoading = ref(false)
const preventRejectionModalClose = ref(false)

// Adicionar refs adicionais para o modal de tempo de entrega
const deliveryTimeModalOpen = ref(false)
const pedidoToDelivery = ref(null)
const deliveryTimeLoading = ref(false)

// Add this new ref for loading state
const loadingRemoveMereceAtencao = ref(false)

export default function usePedidoActions() {
  const pedidoStore = usePedidoStore()
  const revendedorStore = useRevendedorStore() // Add this
  const { confirm, setLoading } = useConfirmation()

  /**
   * Displays a notification with the given message
   */
  const showNotification = (message, duration = 2000) => {
    // Set message first
    copyMessage.value = message
    // Then show notification
    showCopyNotification.value = true

    // Ensure we clear the notification after duration
    setTimeout(() => {
      showCopyNotification.value = false
    }, duration)
  }

  /**
   * Displays an error notification using the store's errorSnackbar
   */
  const showErrorNotification = (message) => {
    Object.assign(pedidoStore.errorSnackbar, {
      show: true,
      message: message,
      color: 'error',
      timeout: 5000,
    })
  }

  /**
   * Copies text to clipboard and shows notification
   */
  const copyToClipboard = (text, label = 'Texto') => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showNotification(`${label} copiado!`)
      })
      .catch((err) => {
        console.error('Erro ao copiar para o clipboard:', err)
        showErrorNotification('Erro ao copiar para o clipboard')
      })
  }

  /**
   * Opens WhatsApp to send notification
   */
  const openWhatsApp = (telefone, contactName, pedidoId, tipo) => {
    if (!telefone) return

    let text =
      tipo === 'revenda'
        ? `Olá ${contactName} você tem um novo pedido no aplicativo Preço do Gás, *acesse o sistema para ver os detalhes do pedido*: https://revendedor.precodogas.com.br/`
        : `Olá ${contactName} obrigado por fazer um pedido na Preço do Gas. Para acompanhar seu pedido acesse o site nesse link: https://www.precodogas.com.br/consumidor-detalhe-pedido.html?tokenP=${token}. *O Entregador Está a Caminho* Obrigado.`

    const encodedText = encodeURIComponent(text)
    const telefoneFormatado = '55' + telefone.replace(/\D/g, '')
    const url = `https://api.whatsapp.com/send?phone=${telefoneFormatado}&text=${encodedText}`
    window.open(url)
  }

  /**
   * Notificar revenda novo pedido para aceitar
   */
  const handleAvisarPedido = async (pedido) => {
    if (!pedido?.id || !pedido?.revenda?.revendedor?.id) return

    try {
      setPedidoOperationLoading(pedido.id, 'avisar-pedido', true)

      const response = await pedidoStore.avisarPedido(pedido.id, pedido.revenda.revendedor.id)

      if (response.success) {
        showNotification(response.message)
      } else {
        showErrorNotification(response.message || 'Erro ao notificar pedido')
      }
    } catch (error) {
      console.error('Erro ao avisar pedido:', error)
      showErrorNotification('Erro ao notificar pedido')
    } finally {
      setPedidoOperationLoading(pedido.id, 'avisar-pedido', false)
    }
  }

  /**
   * Notifies customer that delivery person is at the door
   */
  const handleNotificarEstouNaPorta = async (pedido) => {
    if (!pedido?.id) return

    loadNotificarEstouNaPorta.value = true

    try {
      const response = await pedidoStore.notificarEstouNaPorta(pedido.id)

      if (response.success) {
        showNotification(response.message)
      } else {
        showErrorNotification(response.message || 'Erro ao notificar "Estou na porta"')
      }
    } catch (error) {
      console.error('Erro ao notificar "Estou na porta":', error)
      showErrorNotification('Erro ao notificar "Estou na porta"')
    } finally {
      loadNotificarEstouNaPorta.value = false
    }
  }

  /**
   * Blocks or unblocks a client
   */
  const handleClienteBlock = async (pedido) => {
    if (!pedido?.cliente?.id) return

    const isBlocking = !pedido.cliente.bloqueado
    loadingClienteBlock.value = true

    try {
      const response = isBlocking
        ? await pedidoStore.bloquearCliente(pedido.cliente.id)
        : await pedidoStore.desbloquearCliente(pedido.cliente.id)

      if (response.success) {
        pedido.cliente.bloqueado = isBlocking
        showNotification(response.message)
      } else {
        showErrorNotification(
          response.message || `Erro ao ${isBlocking ? 'bloquear' : 'desbloquear'} cliente`,
        )
      }
    } catch (error) {
      console.error(`Erro ao ${isBlocking ? 'bloquear' : 'desbloquear'} cliente:`, error)
      showErrorNotification(`Erro ao ${isBlocking ? 'bloquear' : 'desbloquear'} cliente`)
    } finally {
      loadingClienteBlock.value = false
    }
  }

  /**
   * Ativar revenda
   */
  const ativarRevenda = async (pedido) => {
    if (!pedido?.revenda?.revendedor?.id) return

    loadingRevendedorAction.value = true

    try {
      // Use revendedorStore instead of pedidoStore
      const response = await revendedorStore.ativarRevendedor(pedido.revenda.revendedor.id)

      if (response.success) {
        pedido.revenda.revendedor.ativo = 1
        showNotification(response.message || 'Revendedor ativado com sucesso!')
      } else {
        showErrorNotification(response.message || 'Erro ao ativar revendedor.')
      }
    } catch (error) {
      console.error('Erro ao ativar revendedor:', error)
      showErrorNotification('Erro ao ativar revendedor.')
    } finally {
      loadingRevendedorAction.value = false
    }
  }

  /**
   * Desativar revenda com duração
   * useModalManagement para evitar dependência circular
   */
  const desativarRevenda = async (durationType, revenda, closeModalCallback) => {
    if (!revenda?.revendedor?.id) return

    loadingRevendedorAction.value = true

    try {
      // Use revendedorStore instead of pedidoStore
      const response = await revendedorStore.desativarRevendedor(
        revenda.revendedor.id,
        durationType,
      )

      if (response.success) {
        revenda.revendedor.ativo = 0
        showNotification(response.message)
        if (closeModalCallback) closeModalCallback()
      } else {
        showErrorNotification(response.message || 'Erro ao desativar revendedor')
      }

      return response.success
    } catch (error) {
      console.error('Erro ao desativar revendedor:', error)
      showErrorNotification('Erro ao desativar revendedor')
      return false
    } finally {
      loadingRevendedorAction.value = false
    }
  }

  /**
   * Alterar status de pedido pendente
   */
  const handleTogglePendente = async (pedido) => {
    if (!pedido?.id) return

    const operationKey = 'pendente'
    setPedidoOperationLoading(pedido.id, operationKey, true)

    try {
      const response = await pedidoStore.atualizarPedidoPendente(pedido.id)

      if (response.success) {
        pedido.pendente = pedido.pendente ? 0 : 1
        showNotification(response.message || 'Status de pedido pendente atualizado')
      } else {
        showErrorNotification(response.message || 'Erro ao atualizar status pendente.')
      }
    } catch (error) {
      console.error('Erro ao atualizar status pendente:', error)
      showErrorNotification('Erro ao atualizar status pendente.')
    } finally {
      setPedidoOperationLoading(pedido.id, operationKey, false)
    }
  }

  /**
   * Handles applying solution to a pedido
   */
  const handleAplicarSolucao = async (pedido, idacao) => {
    if (!pedido?.id || !idacao) return false

    const solutionMessages = {
      1: 'avisar pedido',
      2: 'ligar para cliente',
      3: 'salvar pedido',
      4: 'verificar pedidos atrasados',
    }

    const action = solutionMessages[idacao] || 'aplicar solução'

    // Track loading state with a unique key
    const operationKey = `solucao-${idacao}`
    setPedidoOperationLoading(pedido.id, operationKey, true)

    try {
      const response = await pedidoStore.aplicarSolucaoPedido(pedido.id, idacao)

      if (response.success) {
        // Toggle pendente flag if idacao is 1
        if (idacao === '1') {
          pedido.pendente = pedido.pendente ? 0 : 1
        }

        showNotification(response.message || `Ação "${action}" aplicada com sucesso!`)
        return true
      } else {
        showErrorNotification(response.message || `Erro ao ${action}.`)
        return false
      }
    } catch (error) {
      console.error(`Erro ao ${action}:`, error)
      showErrorNotification(`Erro ao ${action}.`)
      return false
    } finally {
      setPedidoOperationLoading(pedido.id, operationKey, false)
    }
  }

  /**
   * Handles changing the status of a pedido
   */
  const handleAlterarStatus = async (pedido, statusId) => {
    if (!pedido?.id || !statusId) return false

    if (statusId === '4') {
      // abrir modal de rejeitar pedido
      // Make a deep copy of the pedido to prevent reactivity issues
      pedidoToReject.value = JSON.parse(JSON.stringify(pedido))
      rejectionModalOpen.value = true
      preventRejectionModalClose.value = true

      // Set a timer to allow modal changes after initial render
      setTimeout(() => {
        preventRejectionModalClose.value = false
      }, 500)

      return true // Return true to indicate that the modal is opened
    } else if (statusId === '8') {
      // Open delivery time modal for status 8 (Saiu para entrega)
      pedidoToDelivery.value = JSON.parse(JSON.stringify(pedido))
      deliveryTimeModalOpen.value = true
      return true // Return true to indicate that the modal is opened
    }

    const statusLabels = {
      2: 'aceito',
      4: 'rejeitado',
      8: 'marcado como saiu para entrega',
      3: 'marcado como entregue',
      5: 'cancelado',
    }

    const action = statusLabels[statusId] || 'alterar o status'

    // Track loading state with a unique key
    const operationKey = `status-${statusId}`
    setPedidoOperationLoading(pedido.id, operationKey, true)

    try {
      let response

      // Use specialized endpoint based on status ID
      if (statusId === '2') {
        response = await pedidoStore.aceitarPedido(pedido.id, pedido.revenda?.id)
      } else if (statusId === '3') {
        // Add handling for status 3 (Entregue)
        response = await pedidoStore.pedidoEntregue(pedido.id, pedido.revenda?.id)
      } else if (statusId === '8') {
        // This should never be called directly since we open a modal first
        // We handle it in handleSairParaEntrega
        return true
      } else {
        // For other status changes, use the general endpoint
        // This is where you'd implement the other status endpoints when they're ready
        response = { success: false, message: 'Função ainda não implementada' }
        console.warn('Status change not implemented for statusId:', statusId)
      }

      if (response.success) {
        if (response.data && response.data.escopo_atual) {
          // Find the current column of the pedido
          const currentColumn = pedido.escopo_atual
            ? pedidoStore.getColunaPorEscopo(pedido.escopo_atual)
            : findPedidoCurrentColumn(pedido.id)

          // Remove pedido from its current column if found
          if (currentColumn) {
            pedidoStore.removerPedidoDaColuna(currentColumn, pedido.id)
            console.log(`Pedido removido da coluna ${currentColumn}`)
          } else {
            console.log(`Não foi possível encontrar a coluna atual do pedido ${pedido.id}`)
          }

          // Get target column key from escopo_atual
          const targetColumnKey = pedidoStore.getColunaPorEscopo(response.data.escopo_atual)

          if (targetColumnKey) {
            pedidoStore.adicionarPedidoNaColuna(targetColumnKey, response.data)

            showNotification(`Pedido movido para ${targetColumnKey}`)
          }
        } else if (response.data) {
          // se não houver escopo_atual, atualize o pedido com os novos dados
          Object.assign(pedido, response.data)
        } else {
          // atualizar o status do pedido
          pedido.status = statusId
        }

        showNotification(response.message || `Pedido ${action} com sucesso!`)
        return true
      } else {
        showErrorNotification(response.message || `Erro ao executação ação: pedido ${action}.`)
        return false
      }
    } catch (error) {
      console.error(`Erro ao ${action}:`, error)
      showErrorNotification(`Erro ao ${action}.`)
      return false
    } finally {
      setPedidoOperationLoading(pedido.id, operationKey, false)
    }
  }

  const handleRejectPedido = async (motivoRejeicao) => {
    if (!pedidoToReject.value) return false

    rejectionLoading.value = true
    preventRejectionModalClose.value = true

    try {
      const response = await pedidoStore.rejeitarPedido(
        pedidoToReject.value.id,
        pedidoToReject.value.revenda?.id,
        motivoRejeicao,
      )

      if (response.success) {
        if (response.data && response.data.escopo_atual) {
          // Find the current column of the pedido
          const currentColumn = pedidoToReject.value.escopo_atual
            ? pedidoStore.getColunaPorEscopo(pedidoToReject.value.escopo_atual)
            : findPedidoCurrentColumn(pedidoToReject.value.id)

          // Remove pedido from its current column if found
          if (currentColumn) {
            pedidoStore.removerPedidoDaColuna(currentColumn, pedidoToReject.value.id)
            console.log(`Pedido removido da coluna ${currentColumn}`)
          }

          // Get target column key from escopo_atual
          const targetColumnKey = pedidoStore.getColunaPorEscopo(response.data.escopo_atual)

          if (targetColumnKey) {
            pedidoStore.adicionarPedidoNaColuna(targetColumnKey, response.data)
            showNotification(`Pedido movido para ${targetColumnKey}`)
          }
        } else if (response.data) {
          // Update the pedido with the response data
          Object.assign(pedidoToReject.value, response.data)
        } else {
          // Update only the status
          pedidoToReject.value.status = '4'
        }

        showNotification(response.message || 'Pedido rejeitado com sucesso!')
        rejectionModalOpen.value = false
        return true
      } else {
        showErrorNotification(response.message || 'Erro ao rejeitar pedido.')
        return false
      }
    } catch (error) {
      console.error('Erro ao rejeitar pedido:', error)
      showErrorNotification('Erro ao rejeitar pedido.')
      return false
    } finally {
      rejectionLoading.value = false
      setTimeout(() => {
        // Close modal only after API call completes
        rejectionModalOpen.value = false
        // Allow a small delay before releasing the lock
        setTimeout(() => {
          preventRejectionModalClose.value = false
          pedidoToReject.value = null
        }, 100)
      }, 100)
    }
  }

  /**
   * Handle remake pedido with a reason
   */
  const handleRemakePedido = async (motivoRejeicao) => {
    if (!pedidoToReject.value) return false

    rejectionLoading.value = true
    preventRejectionModalClose.value = true

    try {
      const response = await pedidoStore.refazerPedido(
        pedidoToReject.value.id,
        pedidoToReject.value.revenda?.id,
        motivoRejeicao,
      )

      if (response.success) {
        if (response.data) {
          // Update the pedido with the response data if it exists
          if (response.data.pedido_rejeitado) {
            Object.assign(pedidoToReject.value, response.data.pedido_rejeitado)
          }

          if (response.data.novo_pedido_id) {
            showNotification(
              `Pedido rejeitado e refeito com sucesso! Novo pedido: #${response.data.novo_pedido_id}`,
            )
          } else {
            showNotification(
              response.message || 'Pedido rejeitado com sucesso! Mas não foi possível refazê-lo.',
            )
          }
        } else {
          // apenas atualizar estado do pedido
          pedidoToReject.value.status = '4'
        }

        rejectionModalOpen.value = false
        return true
      } else {
        showErrorNotification(response.message || 'Erro ao refazer pedido.')
        return false
      }
    } catch (error) {
      console.error('Erro ao refazer pedido:', error)
      showErrorNotification('Erro ao refazer pedido.')
      return false
    } finally {
      rejectionLoading.value = false
      setTimeout(() => {
        // Close modal only after API call completes
        rejectionModalOpen.value = false
        // Allow a small delay before releasing the lock
        setTimeout(() => {
          preventRejectionModalClose.value = false
          pedidoToReject.value = null
        }, 100)
      }, 100)
    }
  }

  /**
   * Handle pedido delivery with delivery time
   */
  const handleSairParaEntrega = async (minutes) => {
    if (!pedidoToDelivery.value) return false

    deliveryTimeLoading.value = true

    try {
      // Store this value as the new default for next time
      sessionStorage.setItem('lastDeliveryTime', minutes.toString())

      const response = await pedidoStore.sairParaEntrega(
        pedidoToDelivery.value.id,
        pedidoToDelivery.value.revenda?.id,
        minutes,
      )

      if (response.success) {
        if (response.data && response.data.escopo_atual) {
          // Find the current column of the pedido
          const currentColumn = pedidoToDelivery.value.escopo_atual
            ? pedidoStore.getColunaPorEscopo(pedidoToDelivery.value.escopo_atual)
            : findPedidoCurrentColumn(pedidoToDelivery.value.id)

          // Remove pedido from its current column if found
          if (currentColumn) {
            pedidoStore.removerPedidoDaColuna(currentColumn, pedidoToDelivery.value.id)
            console.log(`Pedido removido da coluna ${currentColumn}`)
          }

          // Get target column key from escopo_atual
          const targetColumnKey = pedidoStore.getColunaPorEscopo(response.data.escopo_atual)

          if (targetColumnKey) {
            pedidoStore.adicionarPedidoNaColuna(targetColumnKey, response.data)
            showNotification(`Pedido movido para ${targetColumnKey}`)
          }
        } else if (response.data) {
          // Update the pedido with the response data
          Object.assign(pedidoToDelivery.value, response.data)
        } else {
          // Update only the status
          pedidoToDelivery.value.status = '8'
        }

        showNotification(response.message || 'Pedido marcado como saiu para entrega com sucesso!')
        deliveryTimeModalOpen.value = false
        return true
      } else {
        showErrorNotification(response.message || 'Erro ao marcar pedido como saiu para entrega.')
        return false
      }
    } catch (error) {
      console.error('Erro ao marcar pedido como saiu para entrega:', error)
      showErrorNotification('Erro ao marcar pedido como saiu para entrega.')
      return false
    } finally {
      deliveryTimeLoading.value = false
      setTimeout(() => {
        // Close modal only after API call completes
        deliveryTimeModalOpen.value = false
        // Allow a small delay before releasing
        setTimeout(() => {
          pedidoToDelivery.value = null
        }, 100)
      }, 100)
    }
  }

  /**
   * Find the current column of a pedido by searching through all columns
   */
  const findPedidoCurrentColumn = (pedidoId) => {
    // Get all visible column keys
    const columnKeys = pedidoStore.statusColumns
      .filter((column) => column.visible)
      .map((column) => column.key)

    // Search for the pedido in each column
    for (const columnKey of columnKeys) {
      const pedidos = pedidoStore.getPedidosByStatus(columnKey)
      if (pedidos.some((p) => p.id === pedidoId)) {
        return columnKey
      }
    }

    // Return null if not found in any column
    return null
  }

  /**
   * Check if a specific pedido operation is loading
   */
  const isPedidoOperationLoading = (pedidoId, operation) => {
    const key = `${pedidoId}-${operation}`
    return pendingLoadingStates.value.get(key) || false
  }

  /**
   * estato de carregamento de operação de pedido
   */
  const setPedidoOperationLoading = (pedidoId, operation, isLoading) => {
    const key = `${pedidoId}-${operation}`
    if (isLoading) {
      pendingLoadingStates.value.set(key, true)
    } else {
      pendingLoadingStates.value.delete(key)
    }
  }

  const isAnyModalOpen = () => {
    return rejectionModalOpen.value || deliveryTimeModalOpen.value
  }

  watch(
    () => pedidoStore.lastUpdated,
    () => {
      if (isAnyModalOpen()) {
        console.log('Preventing modals from closing during automatic update')
      }
    },
    { deep: true },
  )

  const handleNotificarAtraso = async (pedido) => {
    if (!pedido || !pedido.id) {
      showErrorNotification('Pedido não encontrado')
      return false
    }

    setPedidoOperationLoading(pedido.id, 'notificar-atraso', true)

    try {
      const response = await pedidoStore.notificarAtraso(pedido.id)

      if (response.success) {
        showNotification(response.message || 'Notificação de atraso enviada com sucesso!')
        return true
      } else {
        showErrorNotification(response.message || 'Falha ao enviar notificação de atraso')
        return false
      }
    } catch (error) {
      console.error('Erro ao enviar notificação de atraso:', error)
      showErrorNotification('Erro ao enviar notificação de atraso')
      return false
    } finally {
      setPedidoOperationLoading(pedido.id, 'notificar-atraso', false)
    }
  }

  // Replace this function to call pedidoStore instead of API directly
  const removerMereceAtencaoCliente = async (clienteId) => {
    if (!clienteId) {
      return {
        success: false,
        message: 'ID do cliente não informado',
      }
    }

    loadingRemoveMereceAtencao.value = true

    try {
      // Call the store method instead of using API directly
      const response = await pedidoStore.removerMereceAtencaoCliente(clienteId)
      return response
    } catch (error) {
      console.error('Erro ao remover merece atenção do cliente:', error)
      return {
        success: false,
        message: 'Erro ao remover indicação de atenção especial.',
        error,
      }
    } finally {
      loadingRemoveMereceAtencao.value = false
    }
  }

  return {
    showCopyNotification,
    copyMessage,
    loadNotificarPedido,
    loadNotificarEstouNaPorta,
    loadingClienteBlock,
    loadingRevendedorAction,
    rejectionModalOpen,
    rejectionLoading,
    pedidoToReject,
    preventRejectionModalClose,
    deliveryTimeModalOpen,
    deliveryTimeLoading,
    pedidoToDelivery,
    loadingRemoveMereceAtencao,

    showNotification,
    showErrorNotification, // Add this to the return object
    copyToClipboard,
    openWhatsApp,
    handleAvisarPedido,
    handleNotificarEstouNaPorta,
    handleClienteBlock,
    ativarRevenda,
    desativarRevenda,
    handleTogglePendente,
    handleAplicarSolucao,
    handleAlterarStatus,
    handleRejectPedido,
    handleRemakePedido,
    handleSairParaEntrega,
    isPedidoOperationLoading,
    setPedidoOperationLoading,
    isAnyModalOpen,
    handleNotificarAtraso,
    removerMereceAtencaoCliente,
  }
}
